// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { jsx, ParamComponent } from 'panel';
import { selx } from 'clsx';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { DashboardThumbnailContents, MediaThumbnailInfo, ReportThumbnailInfo } from 'common/report/util/search/types';
import { ContentType, LayoutCell, LayoutResponse, LayoutRowResponse, MediaCardType } from 'common/report/dashboards/types';
import { getOrderedGridRows } from 'common/report/util/dashboards/grid-layout';
import { getMediaCardImageSrc } from 'common/report/dashboards/media-card';
import { getEntityImage } from '../util';
import TEXT_CARD_IMAGE_GRAY from '../images/grayscale/text-card.svg';
import TEXT_CARD_IMAGE_HOVERING from '../images/hovering/text-card.svg';
import VIDEO_CARD_IMAGE_GRAY from '../images/grayscale/video-card.svg';
import VIDEO_CARD_IMAGE_HOVERING from '../images/hovering/video-card.svg';
import DASHBOARD_CARD_IMAGE_GRAY from '../images/grayscale/report-types/boards.svg';
import DASHBOARD_CARD_IMAGE_HOVERING from '../images/hovering/report-types/boards.svg';
import css from './index.module.styl?inline';
import styles from './index.module.styl';
export enum DashboardThumbnailRowSize {
  Small = "S",
  Large = "L",
}
type DashboardThumbnailParams = {
  layout: LayoutResponse;
  contents: DashboardThumbnailContents;
  rowSize?: DashboardThumbnailRowSize;
  isGrayscale?: boolean;
  isHovering?: boolean;
};
type ThumbnailInfo = MediaThumbnailInfo | ReportThumbnailInfo | undefined;
function isMediaThumbnailInfo(thumbnailInfo: ThumbnailInfo): thumbnailInfo is MediaThumbnailInfo {
  return (thumbnailInfo as MediaThumbnailInfo === null || thumbnailInfo as MediaThumbnailInfo === void 0 ? void 0 : (thumbnailInfo as MediaThumbnailInfo).media_type) !== undefined;
}
function isReportThumbnailInfo(thumbnailInfo: ThumbnailInfo): thumbnailInfo is ReportThumbnailInfo {
  return (thumbnailInfo as ReportThumbnailInfo === null || thumbnailInfo as ReportThumbnailInfo === void 0 ? void 0 : (thumbnailInfo as ReportThumbnailInfo).chart_type) !== undefined;
}
function getImageForCell(cell: LayoutCell, thumbnailInfo: ThumbnailInfo, isGrayscale: boolean, isHovering: boolean) {
  if (cell.content_type === ContentType.Text) {
    if (isHovering) {
      return TEXT_CARD_IMAGE_HOVERING;
    } else if (isGrayscale) {
      return TEXT_CARD_IMAGE_GRAY;
    }
    return TEXT_CARD_IMAGE_GRAY;
  }
  if (cell.content_type === ContentType.Media && isMediaThumbnailInfo(thumbnailInfo)) {
    if (thumbnailInfo.media_type === MediaCardType.Image) {
      return getMediaCardImageSrc(thumbnailInfo.path);
    }
    if (isHovering) {
      return VIDEO_CARD_IMAGE_HOVERING;
    }
    return VIDEO_CARD_IMAGE_GRAY;
  }
  if ([ContentType.Report, ContentType.ReportLink].includes(cell.content_type) && isReportThumbnailInfo(thumbnailInfo)) {
    return getEntityImage(thumbnailInfo.chart_type, thumbnailInfo.bookmark_type, isGrayscale, isHovering);
  }
  if (cell.content_type === ContentType.Dashboard) {
    if (isHovering) {
      return DASHBOARD_CARD_IMAGE_HOVERING;
    } else if (isGrayscale) {
      return DASHBOARD_CARD_IMAGE_GRAY;
    }
    return getEntityImage(null, "dashboard");
  }
  return getEntityImage();
}
function GridRowThumbnail(_ref: {
  row: LayoutRowResponse;
  contents: DashboardThumbnailContents;
  size: DashboardThumbnailRowSize;
  isGrayscale: boolean;
  isHovering: boolean;
}) {
  let {
    row,
    contents,
    size,
    isGrayscale,
    isHovering
  }: {
    row: LayoutRowResponse;
    contents: DashboardThumbnailContents;
    size: DashboardThumbnailRowSize;
    isGrayscale: boolean;
    isHovering: boolean;
  } = _ref;
  let columnStart = 1;
  return <>
      {row.cells.map(cell => {
      var _contents$cell$conten;
      const gridColumn = "".concat(columnStart, " / ").concat(columnStart + cell.width);
      columnStart += cell.width;
      const thumbnailInfo = (_contents$cell$conten = contents[cell.content_type]) === null || _contents$cell$conten === void 0 ? void 0 : _contents$cell$conten[cell.content_id];
      return <div sel={selx(styles.dashboardCard, styles["size".concat(size)])} style={{
        'grid-column': gridColumn
      }}>
            <img sel={selx(styles.dashboardCardBookmarkImage)} class={{
          [styles.isMediaCard]: cell.content_type === "media"
        }} attrs={{
          src: getImageForCell(cell, thumbnailInfo, isGrayscale, isHovering),
          loading: "lazy"
        }} />
          </div>;
    })}
    </>;
}
export class DashboardThumbnail extends ParamComponent<DashboardThumbnailParams> {
  get config() {
    return {
      css,
      params: {
        layout: Object,
        contents: Object,
        rowSize: String,
        isGrayscale: Boolean,
        isHovering: Boolean
      },
      useShadowDom: true,
      template: () => {
        const layoutRows = getOrderedGridRows(this.params.layout);
        return <div sel={selx(styles.dashboardThumbnail)}>
            <div sel={selx(styles.dashboardGrid)}>
              {layoutRows.map(row => {
              return <GridRowThumbnail row={row} contents={this.params.contents} size={this.params.rowSize} isGrayscale={this.params.isGrayscale || false} isHovering={this.params.isHovering || false} />;
            })}
            </div>
          </div>;
      }
    };
  }
}
export const DashboardThumbnailEl = defineAndMakeJsxFactory("mp-dashboard-thumbnail", DashboardThumbnail);
export const DashboardThumbnailReact = panel2react("mp-dashboard-thumbnail", DashboardThumbnail);